import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Alert, Env, EnvName, Host, MetricLatest, Service } from '../api/types';
import { Annunciator } from '../components/Annunciator';
import { EnvSelector } from '../components/EnvSelector';
import { HostInstrument } from '../components/HostInstrument';
import { ServiceTile } from '../components/ServiceTile';

const POLL_MS = 30_000;

export function Dashboard() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [env, setEnv] = useState<EnvName | 'all'>('all');
  const qs = env === 'all' ? '' : `?env=${env}`;

  const { data: envs = [] } = useSWR<Env[]>('/api/envs', fetcher, { refreshInterval: POLL_MS });
  const { data: hosts = [] } = useSWR<Host[]>(`/api/hosts${qs}`, fetcher, {
    refreshInterval: POLL_MS,
  });
  const { data: services = [] } = useSWR<Service[]>(`/api/services${qs}`, fetcher, {
    refreshInterval: POLL_MS,
  });
  const { data: metrics = [] } = useSWR<MetricLatest[]>(`/api/metrics/latest${qs}`, fetcher, {
    refreshInterval: POLL_MS,
  });
  const { data: activeAlerts = [] } = useSWR<Alert[]>(`/api/alerts/active${qs}`, fetcher, {
    refreshInterval: 15_000,
  });
  const unsilencedAlerts = activeAlerts.filter((a) => !a.silenced);

  const metricsByHost = useMemo(() => {
    const map = new Map<string, MetricLatest[]>();
    for (const m of metrics) {
      const arr = map.get(m.host_id) ?? [];
      arr.push(m);
      map.set(m.host_id, arr);
    }
    return map;
  }, [metrics]);

  return (
    <div className="min-h-screen bg-cockpit-bg text-cockpit-text">
      <header className="sticky top-0 z-10 border-b border-cockpit-border bg-cockpit-bg/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
          <div>
            <h1 className="text-lg font-semibold tracking-tight">QEA Pulse</h1>
            <p className="text-[10px] uppercase tracking-widest text-cockpit-dim">cockpit</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Link
              to="/alerts"
              className={
                unsilencedAlerts.length > 0
                  ? 'rounded border border-cockpit-crit px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-cockpit-crit hover:bg-cockpit-crit/10'
                  : 'rounded border border-cockpit-border px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-cockpit-dim hover:text-cockpit-text'
              }
            >
              Alerts{unsilencedAlerts.length > 0 ? ` · ${unsilencedAlerts.length}` : ''}
            </Link>
            <Link
              to="/config"
              className="rounded border border-cockpit-border px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-cockpit-dim hover:text-cockpit-text"
            >
              Config
            </Link>
            <EnvSelector value={env} envs={envs} onChange={setEnv} />
            <Clock />
            {session.user && (
              <div className="hidden items-center gap-2 border-l border-cockpit-border pl-3 text-xs sm:flex">
                <span className="text-cockpit-dim">
                  {session.user.email}
                  {session.user.role === 'admin' && (
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-cockpit-warn">
                      admin
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    await logout();
                    navigate('/login', { replace: true });
                  }}
                  className="text-cockpit-dim hover:text-cockpit-text"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
        <Annunciator alerts={unsilencedAlerts} />
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-6 py-6">
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-cockpit-dim">
            Hosts
          </h2>
          {hosts.length === 0 ? (
            <p className="text-sm text-cockpit-dim">no hosts in this env</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {hosts.map((h) => (
                <HostInstrument key={h.id} host={h} metrics={metricsByHost.get(h.id) ?? []} />
              ))}
            </div>
          )}
        </section>

        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-cockpit-dim">
            Services
          </h2>
          {services.length === 0 ? (
            <p className="text-sm text-cockpit-dim">no services in this env</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {services.map((s) => (
                <ServiceTile key={s.id} service={s} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="hidden text-right text-xs text-cockpit-dim sm:block">
      <div>{now.toISOString().slice(11, 19)} UTC</div>
      <div className="text-[10px]">{now.toLocaleTimeString()} local</div>
    </div>
  );
}
